/**
 * ListTagRulesPage utilities
 * For Media Devoted | Created By Ciro Autiero
 */

import type { ListTagRule, TagRuleCondition } from '@/hooks';
import { getStatsDateRangeLabel } from '@/components/date-range';
import { formatDateDMY } from '@/features/smswiser-campaigns/utils/dateFormat';

import { LIST_METRIC_FIELDS, OPERATORS, UNGROUPED_KEY } from './constants';

// Overrides are no longer appended inline — the rows show an OverridesBadge
// pill next to the base condition instead; a dozen `country:value` pairs in
// parentheses buried the threshold they modify.
export function formatCondition(c: TagRuleCondition): string {
  const labelOf = (key: string) =>
    LIST_METRIC_FIELDS.find((f) => f.key === key)?.label ?? key;
  const field = labelOf(c.field);
  const op = OPERATORS.find((o) => o.op === c.operator)?.label ?? c.operator;
  const ct = c.compareTo;
  if (ct) {
    const win = ct.dateRangePreset
      ? getStatsDateRangeLabel(ct.dateRangePreset as never)
      : 'rule window';
    const pct = ct.percent === undefined ? '' : `${ct.percent}% of `;
    const src = ct.against === 'total' ? 'TOTAL ' : '';
    return `${field} ${op} ${pct}${src}${labelOf(ct.field)} (${win})`;
  }
  return `${field} ${op} ${c.value}`;
}



/** Groups rules by groupKey; within each group, sorted by orderIndex desc (top = first checked). */
export function groupRules(rules: ListTagRule[]): Array<{ key: string; label: string; rules: ListTagRule[] }> {
  const byGroup = new Map<string, ListTagRule[]>();
  for (const r of rules) {
    const key = (r.groupKey ?? '').trim() || UNGROUPED_KEY;
    const list = byGroup.get(key) ?? [];
    list.push(r);
    byGroup.set(key, list);
  }
  for (const list of byGroup.values()) {
    list.sort((a, b) => (b.orderIndex ?? 0) - (a.orderIndex ?? 0));
  }
  const ordered: Array<{ key: string; label: string; rules: ListTagRule[] }> = [];
  const ungrouped = byGroup.get(UNGROUPED_KEY);
  if (ungrouped?.length) {
    ordered.push({ key: UNGROUPED_KEY, label: 'No group', rules: ungrouped });
  }
  const rest = [...byGroup.entries()].filter(([k]) => k !== UNGROUPED_KEY);
  rest.sort((a, b) => a[0].localeCompare(b[0]));
  for (const [key, rulesList] of rest) {
    ordered.push({ key, label: key, rules: rulesList });
  }
  return ordered;
}

export function getRuleDateLabel(rule: ListTagRule): string {
  if (rule.dateRangePreset === 'custom' && rule.fromDate) {
    if (rule.toDateIsToday === true) {
      return `${formatDateDMY(rule.fromDate)} → Today`;
    }
    if (rule.toDate) {
      return `${formatDateDMY(rule.fromDate)} → ${formatDateDMY(rule.toDate)}`;
    }
  }
  return getStatsDateRangeLabel(rule.dateRangePreset);
}

/** Converts rules to bulk import JSON format for copying into AI prompts. */
export function rulesToBulkImportJson(rules: ListTagRule[]): string {
  const dtos = rules.map((r) => {
    const dto: Record<string, unknown> = {
      name: r.name,
      // Bulk import is value-only -- a compareTo would not round-trip.
      conditions: r.conditions.map(({ compareTo: _ct, ...rest }) => rest),
      dateRangePreset: r.dateRangePreset,
      enabled: r.enabled,
    };
    if (r.color?.trim()) dto.color = r.color.trim();
    if (r.emoji?.trim()) dto.emoji = r.emoji.trim();
    if (r.singleSourceOnly) dto.singleSourceOnly = true;
    if (r.listSystemTags?.length) dto.listSystemTags = r.listSystemTags;
    if (r.groupKey?.trim()) dto.groupKey = r.groupKey.trim();
    if (r.minVolume !== undefined) dto.minVolume = r.minVolume;
    if (r.dateRangePreset === 'custom') {
      if (r.fromDate) dto.fromDate = r.fromDate;
      if (r.toDateIsToday) dto.toDateIsToday = true;
      else if (r.toDate) dto.toDate = r.toDate;
    }
    return dto;
  });
  return JSON.stringify({ rules: dtos }, null, 2);
}

/** ISO UTC → `datetime-local` value in the browser's local zone. */
export function isoToDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso?.trim()) return '';
  const d = new Date(iso.trim());
  if (!Number.isFinite(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Empty input → null (clear setting). Otherwise local datetime → ISO UTC for the API. */
export function datetimeLocalValueToIsoOrNull(local: string): string | null {
  const t = local.trim();
  if (!t) return null;
  const d = new Date(t);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toISOString();
}

export function unusedSinceTimestampsEqual(a: string | null | undefined, b: string | null | undefined): boolean {
  const ta = a?.trim() ? new Date(a.trim()).getTime() : NaN;
  const tb = b?.trim() ? new Date(b.trim()).getTime() : NaN;
  const ea = Number.isFinite(ta);
  const eb = Number.isFinite(tb);
  if (!ea && !eb) return true;
  if (!ea || !eb) return false;
  return ta === tb;
}

export { formatOverrideSuffix } from './format-overrides';
